import React, { useEffect, useCallback, useRef } from 'react'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button';
import { useSelector, useDispatch } from "react-redux";
import Loading from '../../../Components/Loading';
import { Toast } from "primereact/toast";
import BusinessCustomersActions from "../../../Redux/transition/customers/business-customer.reducer";
import { useTranslation, Trans } from "react-i18next";
import { isBatchCompleted, resolveUploadBatchNo } from "../audienceBatchProgress";

const getUploadErrorMessage = (uploadError, t) => {
    if (!uploadError) {
        return t("business_customers.upload_error");
    }

    const fieldErrors = uploadError?.fieldErrors ?? uploadError?.errors;
    if (Array.isArray(fieldErrors) && fieldErrors.length) {
        const messages = fieldErrors
            .map((item) => item?.message || item?.detail || item?.defaultMessage)
            .filter(Boolean);
        if (messages.length) {
            return messages.join(" ");
        }
    }

    if (typeof uploadError?.detail === "string" && uploadError.detail.toLowerCase().includes("segment")) {
        return t("business_customers.audience_import_segment_tag_error");
    }

    return uploadError?.detail || uploadError?.message || uploadError?.title || t("business_customers.upload_error");
};

const Confirmation = ({ audienceName, nonDuplicateNumbers, state, setState, navigateToImportPage, handleBack }) => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();
    const toast = useRef(null);

    const uploadingCustomers = useSelector((state) => state.businessCustomers.uploading);
    const uploadedCustomers = useSelector((state) => state.businessCustomers.uploadCustomer);
    const uploadError = useSelector((state) => state.businessCustomers.uploadError);
    const applying = useSelector((state) => state.businessCustomers.applying);
    const applySuccess = useSelector((state) => state.businessCustomers.appliedImport);
    const applyError = useSelector((state) => state.businessCustomers.appliedImportError);
    const audienceBatches = useSelector((state) => state.businessCustomers.audienceBatches);

    const applyImport = useCallback(
        (id, batchNo) => dispatch(BusinessCustomersActions.businessCustomerApplyRequest(id, batchNo)),
        [dispatch]
    );
    const getAudienceBatches = useCallback(
        (id) => dispatch(BusinessCustomersActions.audienceBatchesRequest(id)),
        [dispatch]
    );
    const validImports = nonDuplicateNumbers.length

    useEffect(() => {
        if (uploadingCustomers) {
            setState((state) => ({ ...state, uploading: true }));
        } else {
            setState((state) => ({ ...state, uploading: false }));
        }
        if (uploadedCustomers) {
            setState((state) => ({ ...state, uploadedCustomers: uploadedCustomers }));
            if (uploadedCustomers.id) {
                getAudienceBatches(uploadedCustomers.id);
            }
        }
        if (!applying && applySuccess !== null && state.applying) {
            showSuccess();
        }
        if (!applying && applyError && state.applying) {
            showError(t("business_customers.apply_error"));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadingCustomers, applying, uploadedCustomers, applyError, applySuccess, state.applying]);

    useEffect(() => {
        if (!uploadingCustomers && uploadError) {
            showError(getUploadErrorMessage(uploadError, t));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadingCustomers, uploadError]);

    const showSuccess = () => {
        toast.current.show({ severity: "success", summary: t("business_customers.success_summary"), detail: t("business_customers.apply_batch_success"), life: 3000 });
    };

    const showError = (message, user) => {
        toast.current.show({ severity: "error", user, summary: t("business_customers.error_summary"), detail: message, life: 3000 });
    };

    const showInfo = (message) => {
        toast.current.show({ severity: "info", summary: t("info"), detail: message, life: 3000 });
    };

    const uploadResult = state.uploadedCustomers || uploadedCustomers;
    const id = uploadResult?.id;
    const uploadData = uploadResult;
    const uploadBatchNo = resolveUploadBatchNo(uploadData, audienceBatches);
    const uploadBatchAlreadyApplied = uploadBatchNo
        ? isBatchCompleted(audienceBatches, uploadBatchNo)
        : false;

    const handleApply = () => {
        if (!uploadBatchNo) {
            showError(t("business_customers.batch_required"));
            return;
        }

        if (uploadBatchAlreadyApplied) {
            showInfo(t("business_customers.batch_already_applied", { batchNo: uploadBatchNo }));
            return;
        }

        applyImport(id, uploadBatchNo);
        setState((state) => ({ ...state, applying: true }));
    }

    const uploadFailed = !uploadingCustomers && !uploadResult && uploadError;
    const waitingForUpload = uploadingCustomers || (state.uploading && !uploadResult && !uploadError);

    return (
        <div>
            <Toast
                ref={toast}
                onRemove={(message) => {
                    if (message.severity === "success") {
                        setState((state) => ({ ...state, applying: false }));
                        navigateToImportPage();
                    }
                }}
            />
            {waitingForUpload ? <Loading />
                : uploadFailed ? (
                    <Card className='main-upload-card'>
                        <div className='pl-4 pr-4 pt-5'>
                            <p className='title-text mb-0'>{t("business_customers.error_summary")}</p>
                            <p className='sub_text mb-4 reveknew_red'>{getUploadErrorMessage(uploadError, t)}</p>
                        </div>
                        <div className='cont-btn'>
                            <Button className='import_cancel_button' onClick={navigateToImportPage}>
                                {t("cancel")}
                            </Button>
                            <Button className='import_button steps-button py-2' onClick={handleBack}>
                                {t("Back")}
                            </Button>
                        </div>
                    </Card>
                ) : uploadResult &&
                <Card className='main-upload-card'>
                    <div className='pl-4 pr-4 pt-5'>
                        <p className='title-text mb-0'>{t("business_customers.import-completed-header")}</p>
                        <p className='sub_text mb-0'>{t("business_customers.completed_text")}</p>

                        <div className='grid mt-4'>
                            <div className='col-5 success-import tip-div completed-tip-div'>
                                <p className='success-import-title'>{t("business_customers.success_completed_import_tip1")}</p>
                                <br />
                                <p className='success-import-text mb-0'>{t("business_customers.success_completed_import_tip2")}</p>
                                <br />
                                <p className='success-import-text2'>
                                    <Trans
                                        i18nKey={validImports > 1 ? "business_customers.success_completed_import_tip3_plural" : "business_customers.success_completed_import_tip3_single"}
                                        values={{ name: audienceName, count: validImports }}
                                        components={{ 1: <span className='success-red' />, 2: <span className='success-green' /> }}
                                    /></p>
                                <br />
                                <p className='success-import-text2'>
                                    <Trans
                                        i18nKey={"business_customers.success_completed_import_tip4"}
                                        components={{ 1: <span className='process-blue' /> }}
                                    /></p>
                                <br />
                                <Button
                                    className='success-import-button'
                                    onClick={() => handleApply()}
                                    loading={applying}
                                    disabled={!uploadBatchNo || uploadBatchAlreadyApplied}
                                >
                                    {uploadBatchAlreadyApplied
                                        ? t("business_customers.batch_already_applied", { batchNo: uploadBatchNo })
                                        : t("business_customers.process_button")}
                                </Button>
                            </div>

                        </div>

                    </div>
                    <div className='cont-btn'>
                        <Button className='import_done' onClick={navigateToImportPage}>
                            {t("done")}
                        </Button>
                        <Button className='import_button steps-button py-2' onClick={handleBack}>
                            {t("business_customers.new_file")}
                        </Button>
                    </div>
                </Card>
            }
        </div>
    )
}

export default Confirmation
